import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { useUnsavedChangesStore } from '@/store/unsavedChangesStore'

export default function UnsavedChangesDialog() {
  const isDirty = useUnsavedChangesStore(state => state.isDirty())
  const blocker = useBlocker(isDirty)

  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return
      e.preventDefault()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const isOpen = blocker.state === 'blocked'

  const handleDiscard = () => {
    if (blocker.state === 'blocked') blocker.proceed()
  }

  const handleStay = () => {
    if (blocker.state === 'blocked') blocker.reset()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleStay}
      maxWidth='xs'
      fullWidth
      sx={{
        borderRadius: borderRadius.lg,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        p: 1
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberRoundedIcon sx={{ color: colors.status.warning, fontSize: '22px', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: fontSizes.lg,
              fontWeight: fontWeights.semibold,
              color: colors.text.primary,
              lineHeight: 1.3
            }}
          >
            Unsaved changes
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1 }}>
        <Typography
          sx={{
            fontSize: fontSizes.base,
            color: colors.text.secondary,
            lineHeight: 1.6
          }}
        >
          You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleStay}
          sx={{
            fontSize: fontSizes.base,
            fontWeight: fontWeights.medium,
            color: colors.text.secondary,
            px: 2,
            '&:hover': { color: colors.text.primary, backgroundColor: colors.grey[100] }
          }}
        >
          Stay on page
        </Button>
        <Button
          onClick={handleDiscard}
          sx={{
            fontSize: fontSizes.base,
            fontWeight: fontWeights.medium,
            backgroundColor: colors.status.error,
            color: colors.text.white,
            px: 2.5,
            borderRadius: borderRadius.lg,
            '&:hover': { backgroundColor: '#e63d10' }
          }}
        >
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}
